import * as Sentry from "@sentry/react";
import cuid from "cuid";

import { createLogger } from "@/logger";

const log = createLogger("reset");

import { Duration } from "luxon";

import { ampli, flags } from "@/config";
import { applyContentRetention, getRetentionConfig } from "@/services/ContentRetention";
import { DatabaseMetrics, DatabaseMetricsService } from "@/services/DatabaseMetrics";
import { applyFeedEviction } from "@/services/FeedEviction";
import { type IdleTransition, InactivityMonitorService } from "@/services/InactivityMonitorService.ts";
import { recordEventAndFlush } from "@/services/telemetry";
import { safeSetItem } from "@/utils/localStorage";

const CaptureMetricsFlag = "ResetCoordinator.CaptureMetrics";
const EnableCheckpointFlag = "ResetCoordinator.EnableCheckpoint";
const EnableEvictionFlag = "ResetCoordinator.EnableEviction";
const EnableContentRetentionFlag = "webWSConfigRetention";

export type ResetCoordinatorConfig = {
  checkpointWalRatio: number;
  intervalMs: number;
};

const defaultConfig: ResetCoordinatorConfig = {
  checkpointWalRatio: 1.5,
  intervalMs: Duration.fromObject({ minutes: 1 }).as("milliseconds"),
};
type UserMetadata = {
  accountId?: string;
  workspaceId?: string;
  workspaceMembershipId?: string;
};

export class ResetCoordinator {
  private static instance: ResetCoordinator | null = null;
  private static readonly CHECKPOINT_COOLDOWN_MS = Duration.fromObject({ minutes: 10 }).as("milliseconds");
  private static readonly CHECKPOINT_IDLE_THRESHOLD_MS = Duration.fromObject({ seconds: 30 }).as("milliseconds");
  private static readonly EVICTION_COOLDOWN_MS = Duration.fromObject({ minutes: 10 }).as("milliseconds");
  private static readonly EVICTION_IDLE_THRESHOLD_MS = Duration.fromObject({ seconds: 30 }).as("milliseconds");
  private static readonly CONTENT_RETENTION_COOLDOWN_MS = Duration.fromObject({ minutes: 10 }).as("milliseconds");
  private static readonly CONTENT_RETENTION_STORAGE_KEY = "lastContentRetentionRun";
  private static readonly EXPENSIVE_METRICS_INTERVAL_MS = Duration.fromObject({ minutes: 10 }).as("milliseconds");

  private config: ResetCoordinatorConfig = defaultConfig;
  private databaseMetricsService: DatabaseMetricsService;
  private contentRetentionRunning = false;
  private evictionRunning = false;
  private inactivityMonitorService: InactivityMonitorService;
  private isStarted: boolean = false;
  private lastCheckpointRunMs = 0;
  private lastEvictionRunMs = 0;
  private lastExpensiveMetricsRunMs = 0;
  private sessionId: string;
  private sessionIndex: number;
  private timer: number | null = null;
  private userMetadata: UserMetadata = {};

  public static getInstance(): ResetCoordinator {
    if (!ResetCoordinator.instance) {
      ResetCoordinator.instance = new ResetCoordinator();
      ResetCoordinator.instance.setDatabaseMetricsService(new DatabaseMetricsService());
      ResetCoordinator.instance.setInactivityMonitorService(new InactivityMonitorService());
    }
    return ResetCoordinator.instance;
  }

  public setConfig(config: Partial<ResetCoordinatorConfig> = {}): void {
    this.config = { ...defaultConfig, ...config };
  }

  public setDatabaseMetricsService(databaseMetricsService: DatabaseMetricsService): void {
    this.databaseMetricsService = databaseMetricsService;
  }

  public setInactivityMonitorService(inactivityMonitorService: InactivityMonitorService): void {
    this.inactivityMonitorService = inactivityMonitorService;
  }

  public updateUserMetadata(partial: UserMetadata): void {
    log.info(`[${this.constructor.name}] Updating user metadata`, { partial });
    this.userMetadata = { ...this.userMetadata, ...partial };
  }

  public async forceCheckpoint(): Promise<{ walBytesBefore: number; walBytesAfter: number | null }> {
    const walBytesBefore = await this.databaseMetricsService.walFileBytes();
    const dbSizeBytes = await this.databaseMetricsService.postgresDatabaseSizeBytes();
    const walBytesAfter = await this.runCheckpoint(walBytesBefore, dbSizeBytes);
    return { walBytesBefore, walBytesAfter };
  }

  public async start(): Promise<void> {
    if (this.isStarted) {
      return;
    }

    this.isStarted = true;

    this.sessionId = cuid();
    this.sessionIndex = 0;
    if (!this.databaseMetricsService) {
      throw new Error("DatabaseMetricsService not set");
    }
    if (!this.inactivityMonitorService) {
      throw new Error("InactivityMonitorService not set");
    }

    this.inactivityMonitorService.start();
    log.info(`[${this.constructor.name}] ResetCoordinator started`, { ...this.config });

    await this.tick();
  }

  private scheduleNext(): void {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(
      () =>
        this.tick().catch((error) => {
          log.error(`[${this.constructor.name}] Error collecting metrics`, { error });
        }),
      this.config.intervalMs,
    );
  }

  private async tick(): Promise<void> {
    const idleTransition = this.inactivityMonitorService.consumeIdleTransition();

    await this.maybeRunCheckpoint();
    await this.maybeRunEviction();
    if (idleTransition) {
      await this.maybeRunContentRetention(idleTransition);
    }
    await this.maybeCollectExpensiveMetrics();
    await this.updateCheapSentryContext();

    this.sessionIndex++;
    this.scheduleNext();
  }

  private reportExpensiveMetricsToSentry(metrics: DatabaseMetrics): void {
    const topTables = Object.entries(metrics.tables)
      .sort(([, a], [, b]) => (b.rowCount ?? 0) - (a.rowCount ?? 0))
      .slice(0, 10)
      .reduce(
        (acc, [name, breakdown]) => {
          acc[`${name}_rows`] = breakdown.rowCount;
          acc[`${name}_bytes`] = breakdown.totalSizeBytes;
          return acc;
        },
        {} as Record<string, number>,
      );

    Sentry.setContext("pglite_tables", topTables);
  }

  private reportCheapMetricsToSentry(cheapMetrics: {
    backendMemoryBytes: number;
    localStorageDatabaseSizeBytes: number | null;
    postgresDatabaseSizeBytes: number;
    walFileBytes: number;
  }): void {
    Sentry.setContext("pglite_database", {
      postgres_size_bytes: cheapMetrics.postgresDatabaseSizeBytes,
      wal_file_bytes: cheapMetrics.walFileBytes,
      backend_memory_bytes: cheapMetrics.backendMemoryBytes,
      indexeddb_size_bytes: cheapMetrics.localStorageDatabaseSizeBytes,
    });
  }

  private async maybeRunEviction(): Promise<void> {
    if (this.evictionRunning) return;

    const evictionEnabled = flags[EnableEvictionFlag];
    if (!evictionEnabled) return;

    const inactivityMs = this.inactivityMonitorService.inactivityDurationMs;
    if (inactivityMs < ResetCoordinator.EVICTION_IDLE_THRESHOLD_MS) return;

    const now = Date.now();
    if (now - this.lastEvictionRunMs < ResetCoordinator.EVICTION_COOLDOWN_MS) return;

    this.evictionRunning = true;
    try {
      const result = await applyFeedEviction();
      this.lastEvictionRunMs = Date.now();

      if (result.deletedItems > 0) {
        log.debug(`[${this.constructor.name}] Feed eviction complete`, {
          deletedItems: result.deletedItems,
          deletedContentIds: result.deletedContentIds,
          affectedFeeds: result.affectedFeeds,
        });
        await recordEventAndFlush("db.eviction.completed", {
          db: {
            eviction: {
              deletedItems: result.deletedItems,
              deletedContentIds: result.deletedContentIds,
              affectedFeeds: result.affectedFeeds,
              user: this.userMetadata,
            },
          },
        });
      }
    } catch (error) {
      log.error(`[${this.constructor.name}] Feed eviction failed`, { error });
    } finally {
      this.evictionRunning = false;
    }
  }

  private async maybeRunContentRetention(trigger: "idle-enter" | "idle-exit"): Promise<void> {
    if (this.contentRetentionRunning) return;

    const retentionEnabled = flags[EnableContentRetentionFlag];
    if (!retentionEnabled) return;

    const retentionConfig = getRetentionConfig();
    if (!retentionConfig) return;

    if (this.isContentRetentionOnCooldown()) return;

    this.contentRetentionRunning = true;
    try {
      log.info(`[${this.constructor.name}] Running content retention (trigger: ${trigger})`, {
        retentionDays: retentionConfig.retentionDays,
      });

      const result = await applyContentRetention(retentionConfig.retentionDays);
      this.saveContentRetentionTimestamp();

      if (result.deletedItems > 0) {
        log.info(`[${this.constructor.name}] Content retention complete`, {
          trigger,
          deletedItems: result.deletedItems,
          deletedContentIds: result.deletedContentIds,
          affectedFeeds: result.affectedFeeds,
          retentionDays: retentionConfig.retentionDays,
        });
        ampli.purgeLocalDatabase({ purgeMethod: "contentRetention" });
        await recordEventAndFlush("db.contentRetention.completed", {
          db: {
            contentRetention: {
              trigger,
              deletedItems: result.deletedItems,
              deletedContentIds: result.deletedContentIds,
              affectedFeeds: result.affectedFeeds,
              retentionDays: retentionConfig.retentionDays,
              user: this.userMetadata,
            },
          },
        });
      }
    } catch (error) {
      log.error(`[${this.constructor.name}] Content retention failed`, { error });
    } finally {
      this.contentRetentionRunning = false;
    }
  }

  private isContentRetentionOnCooldown(): boolean {
    try {
      const stored = localStorage.getItem(ResetCoordinator.CONTENT_RETENTION_STORAGE_KEY);
      if (!stored) return false;
      const lastRunMs = new Date(stored).getTime();
      if (Number.isNaN(lastRunMs)) return false;
      return Date.now() - lastRunMs < ResetCoordinator.CONTENT_RETENTION_COOLDOWN_MS;
    } catch {
      return false;
    }
  }

  private saveContentRetentionTimestamp(): void {
    safeSetItem(localStorage, ResetCoordinator.CONTENT_RETENTION_STORAGE_KEY, new Date().toISOString());
  }

  private async maybeRunCheckpoint(): Promise<void> {
    const checkpointEnabled = flags[EnableCheckpointFlag];
    if (!checkpointEnabled) return;

    const inactivityMs = this.inactivityMonitorService.inactivityDurationMs;
    if (inactivityMs < ResetCoordinator.CHECKPOINT_IDLE_THRESHOLD_MS) return;

    const now = Date.now();
    if (now - this.lastCheckpointRunMs < ResetCoordinator.CHECKPOINT_COOLDOWN_MS) return;

    try {
      const walBytes = await this.databaseMetricsService.walFileBytes();
      const dbSizeBytes = await this.databaseMetricsService.postgresDatabaseSizeBytes();

      if (walBytes > this.config.checkpointWalRatio * dbSizeBytes) {
        await this.runCheckpoint(walBytes, dbSizeBytes);
      }
    } catch (error) {
      log.warn(`[${this.constructor.name}] Checkpoint evaluation failed`, { error });
    }
  }

  private async maybeCollectExpensiveMetrics(): Promise<void> {
    const metricsEnabled = flags[CaptureMetricsFlag];
    if (!metricsEnabled) return;

    const now = Date.now();
    if (now - this.lastExpensiveMetricsRunMs < ResetCoordinator.EXPENSIVE_METRICS_INTERVAL_MS) return;

    try {
      const metrics = await this.databaseMetricsService.collectMetrics();
      this.reportExpensiveMetricsToSentry(metrics);
      this.lastExpensiveMetricsRunMs = now;
      log.debug(`[${this.constructor.name}] Expensive metrics collected and reported to Sentry`);
    } catch (error) {
      log.warn(`[${this.constructor.name}] Failed to collect expensive metrics`, { error });
    }
  }

  private async updateCheapSentryContext(): Promise<void> {
    try {
      const cheapMetrics = await this.databaseMetricsService.collectCheapMetrics();
      this.reportCheapMetricsToSentry(cheapMetrics);
    } catch (error) {
      log.warn(`[${this.constructor.name}] Failed to update cheap Sentry context`, { error });
    }
  }

  private async runCheckpoint(walBytes: number, dbSizeBytes: number): Promise<number | null> {
    try {
      const start = performance.now();
      await this.databaseMetricsService.checkpoint();
      const walBytesAfter = await this.databaseMetricsService.walFileBytes();
      const durationMs = Math.round(performance.now() - start);
      this.lastCheckpointRunMs = Date.now();

      log.info(`[${this.constructor.name}] WAL checkpoint completed`, {
        walBytesBefore: walBytes,
        walBytesAfter,
        dbSizeBytes,
        durationMs,
      });

      await recordEventAndFlush("db.checkpoint.completed", {
        db: {
          checkpoint: {
            walBytesBefore: walBytes,
            walBytesAfter,
            dbSizeBytes,
            durationMs,
            user: this.userMetadata,
            sessionId: this.sessionId,
            sessionIndex: this.sessionIndex,
          },
        },
      });

      Sentry.addBreadcrumb({
        category: "db.checkpoint",
        message: "WAL checkpoint completed",
        level: "info",
        data: {
          walBytesBefore: walBytes,
          walBytesAfter,
          dbSizeBytes,
          durationMs,
        },
      });

      return walBytesAfter;
    } catch (error) {
      log.warn(`[${this.constructor.name}] WAL checkpoint failed`, { error });
      return null;
    }
  }
}
